import jszip from 'jszip';
import container from '@/container';
import { FetchServiceContract, CONTRACTS, AuthServiceContract } from '@/core/contracts';
import { ValidationResult } from './types';
import { WarningCollection, ApiUploadOptions, ApiResponse } from './apiTypes';
import { ContextsState } from '@/store';
import { encodeParams, uploadFile } from '@/core/helpers';
import config from '@/config';

export function openFilesDialog(contentType: string, multiple: boolean): Promise<File[]> {
    return new Promise(resolve => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = multiple;
        input.accept = contentType;

        input.onchange = () => {
            const files = Array.from(input.files || []);
            resolve(files);
        };

        input.click();
    });
}
export function prepareData(files: File[]): Promise<File> {
    const zipFile = getFirstZipFile(files);

    if (zipFile) {
        return new Promise(resolve => {
                resolve(zipFile);
            }
        );
    }

    const compatibleFiles = getCompatibleFiles(files);

    if (compatibleFiles.length === 0) {
        return Promise.reject();
    }

    const zip = new jszip();

    for (const file of compatibleFiles) {
        zip.file(file.name, file);
    }

    return zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 }
    }).then(file =>
        new Promise(resolve => {
            return resolve(blobToFile(file, 'zipData'));
        })
    );
}

export function validateData(data: File): Promise<ValidationResult> {
    return uploadContext(data, { trackStages: false })
        .then(response => {
            return {
                data,
                response
            };
        });
}

export function commitData(validationResult: ValidationResult): Promise<any> {
    return uploadContext(
        validationResult.data,
        {
            confirmedWarnings: validationResult.response.hash,
            refreshContext: true,
            trackStages: false
        }
    );
}

export function filterNonEmptyWarnings(warnings: WarningCollection[]) {
    return warnings
        .filter(x => x.classWarnings.length > 0 || x.objectWarnings.length > 0);
}

export function getFirstZipFile(files: File[]): File | undefined {
    const zipTypes = ['application/x-zip-compressed', 'application/zip'];
    return files.filter(file => zipTypes.indexOf(file.type) >= 0, files)[0];
}

export function blobToFile(blob: Blob, fileName: string): File {
    (blob as any).lastModifiedDate = new Date();
    (blob as any).name = fileName;

    return blob as File;
}

function getCompatibleFiles(files: File[]): File[] {
    const compatibleTypes = ['application/json'];
    return files.filter(file => compatibleTypes.indexOf(file.type) >= 0, files);
}

export function uploadContext(
    file: File,
    options: ApiUploadOptions
): Promise<ApiResponse> {
    const currentContext = ContextsState.currentContextId;
    const fetchService = container.get<FetchServiceContract>(CONTRACTS.FetchService);
    const url = fetchService.buildAbsoluteUrl(`/api/v2/files/contexts/${currentContext}/zip?${encodeParams(options)}`);
    const timeout = config.ContextUploadTimeout;
    return uploadFile<ApiResponse>(file, url, () => {}, timeout);
}
